// EmargementModal.jsx — Phase C : émargement d'un événement
// Mode QR scan (Html5Qrcode) + Mode liste manuelle (checkboxes)
import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { api } from '@/lib/api';
import { downloadAuthed } from '@/lib/media';
import { toast } from 'sonner';
import {
  X, QrCode, Users, Check, Loader2, Search, UserCheck, UserX,
  Camera, CameraOff, RefreshCw, AlertCircle, Sparkles, MapPin, Calendar, Download,
} from 'lucide-react';

export default function EmargementModal({ evenementId, onClose }) {
  const [tab, setTab] = useState('manuel');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/presences/evenement/${evenementId}`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [evenementId]);

  const togglePresence = async (item) => {
    const key = item.billet_id || item.user_id || item.inscription_id;
    setSavingId(key);
    try {
      const body = { present: !item.present };
      if (item.billet_id) body.billet_id = item.billet_id;
      else if (item.user_id) body.user_id = item.user_id;
      else if (item.inscription_id) body.inscription_id = item.inscription_id;
      await api.post(`/presences/evenement/${evenementId}/marquer`, body);
      // Optimistic update
      setData((d) => ({
        ...d,
        list: d.list.map((l) => {
          const lk = l.billet_id || l.user_id || l.inscription_id;
          if (lk === key) return { ...l, present: !item.present, checked_in_at: !item.present ? new Date().toISOString() : null, method: !item.present ? 'manual' : null };
          return l;
        }),
        stats: {
          ...d.stats,
          presents: d.stats.presents + (!item.present ? 1 : -1),
          absents: d.stats.absents + (item.present ? 1 : -1),
        },
      }));
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setSavingId(null); }
  };

  const filtered = data?.list?.filter((l) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.prenom || '').toLowerCase().includes(q) ||
           (l.nom || '').toLowerCase().includes(q) ||
           (l.email || '').toLowerCase().includes(q);
  }) || [];

  const exportPdf = async () => {
    try {
      await downloadAuthed(`/api/presences/evenement/${evenementId}/pdf`, 'liste-presence.pdf');
    } catch { toast.error('Export PDF impossible'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-md flex items-start justify-end fade-in" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-3xl h-screen overflow-y-auto shadow-2xl flex flex-col" data-testid="emargement-modal">

        {/* Header */}
        <header className="sticky top-0 z-10 bg-white border-b border-[#E2E8F0] p-5 sm:p-6">
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-[#F1F5F9] grid place-items-center" data-testid="emargement-close"><X className="w-4 h-4" /></button>
          <button
            onClick={exportPdf}
            className="absolute top-4 right-14 inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] text-xs font-semibold text-[#0F172A]"
            data-testid="emargement-export-pdf"
          >
            <Download className="w-3.5 h-3.5" /> PDF
          </button>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--theme-primary)' }}>Émargement</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A] leading-tight pr-10">
            {data?.evenement?.nom || <span className="opacity-30">Chargement…</span>}
          </h2>
          {data?.evenement && (
            <div className="flex items-center gap-3 mt-2 text-xs text-[#475569] flex-wrap">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(data.evenement.date_debut).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</span>
              {data.evenement.lieu && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {data.evenement.lieu}</span>}
            </div>
          )}
        </header>

        {/* Stats */}
        {data?.stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 sm:px-6 py-4 bg-[#F9F9F8] border-b border-[#E2E8F0]" data-testid="emargement-stats">
            <StatBox label="Inscrits" value={data.stats.total_inscrits} />
            <StatBox label="Présents" value={data.stats.presents} highlight="success" />
            <StatBox label="Absents" value={data.stats.absents} />
            <StatBox label="Adhérents" value={data.stats.adherents} sub={`${data.stats.non_adherents} non-adhérents`} />
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-[#E2E8F0] px-5 sm:px-6">
          <TabBtn active={tab === 'manuel'} onClick={() => setTab('manuel')} testid="tab-manuel">
            <Users className="w-4 h-4" /> Liste manuelle
          </TabBtn>
          <TabBtn active={tab === 'qr'} onClick={() => setTab('qr')} testid="tab-qr">
            <QrCode className="w-4 h-4" /> Scan QR
          </TabBtn>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          {loading ? (
            <div className="py-16 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
          ) : tab === 'manuel' ? (
            <>
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-3 mb-4 flex items-center gap-3">
                <Search className="w-4 h-4 text-[#94A3B8]" />
                <input
                  type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher (nom, email)…" className="flex-1 outline-none text-sm bg-transparent"
                  data-testid="emargement-search"
                />
              </div>
              {filtered.length === 0 ? (
                <EmptyList />
              ) : (
                <div className="space-y-2" data-testid="emargement-list">
                  {filtered.map((l) => {
                    const key = l.billet_id || l.user_id || l.inscription_id || `${l.email}-${l.source}`;
                    const saving = savingId === (l.billet_id || l.user_id || l.inscription_id);
                    return (
                      <PersonRow key={key} item={l} onToggle={() => togglePresence(l)} saving={saving} />
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <ScanTab evenementId={evenementId} onScanned={() => load()} />
          )}
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, sub, highlight }) {
  const c = highlight === 'success' ? '#10B981' : 'var(--theme-primary)';
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-1">{label}</div>
      <div className="font-display text-2xl sm:text-3xl font-black tracking-tight" style={{ color: c }}>{value}</div>
      {sub && <div className="text-[10px] text-[#94A3B8] mt-0.5">{sub}</div>}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`relative flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors ${active ? 'text-[#0F172A]' : 'text-[#94A3B8] hover:text-[#475569]'}`}>
      {children}
      {active && <span className="absolute -bottom-px inset-x-0 h-0.5 bg-[var(--theme-primary)]" />}
    </button>
  );
}

function PersonRow({ item, onToggle, saving }) {
  const initials = (item.prenom?.[0] || '?') + (item.nom?.[0] || '');
  return (
    <div className={`flex items-center gap-3 p-3 rounded-2xl border transition-colors ${item.present ? 'bg-[#10B981]/5 border-[#10B981]/30' : 'bg-white border-[#E2E8F0] hover:bg-[#F9F9F8]'}`} data-testid={`emargement-row-${item.billet_id || item.user_id || item.inscription_id || item.email}`}>
      <div className={`w-10 h-10 rounded-full grid place-items-center font-display font-black text-xs shrink-0 ${item.present ? 'bg-[#10B981] text-white' : 'bg-[#F1F5F9] text-[#475569]'}`}>
        {item.present ? <Check className="w-5 h-5" /> : initials.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-[#0F172A] truncate">{item.prenom} {item.nom || (!item.prenom && '(Inconnu)')}</div>
          {item.adherent ? <span className="badge badge-success">Adhérent</span> : <span className="badge badge-neutral">Non adhérent</span>}
          {item.source === 'inscription_publique' && <span className="badge badge-info">Inscription publique</span>}
        </div>
        <div className="text-xs text-[#94A3B8] truncate">{item.email || 'Pas d\'email'}</div>
        {item.present && item.checked_in_at && (
          <div className="text-[10px] text-[#10B981] mt-0.5 flex items-center gap-1">
            <Check className="w-2.5 h-2.5" /> Émargé{item.method === 'qr' ? ' via QR' : ' manuellement'} à {new Date(item.checked_in_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>
      <button
        onClick={onToggle}
        disabled={saving}
        className={`shrink-0 px-3 py-2 rounded-xl font-semibold text-xs transition-all ${item.present ? 'bg-[#FEE2E2] text-[#DC2626] hover:bg-[#FECACA]' : 'bg-[var(--theme-primary)] text-white hover:opacity-90'}`}
        data-testid={`toggle-${item.billet_id || item.user_id || item.inscription_id}`}
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : item.present ? <><UserX className="w-3.5 h-3.5 inline mr-1" /> Retirer</> : <><UserCheck className="w-3.5 h-3.5 inline mr-1" /> Émarger</>}
      </button>
    </div>
  );
}

function EmptyList() {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-3xl p-12 text-center">
      <Users className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
      <h3 className="font-display text-lg font-bold text-[#0F172A] mb-2">Aucun inscrit</h3>
      <p className="text-sm text-[#475569]">Personne n'est encore inscrit à cet événement.</p>
    </div>
  );
}

function ScanTab({ evenementId, onScanned }) {
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [cameraError, setCameraError] = useState(null);
  const [manual, setManual] = useState('');
  const scannerRef = useRef(null);
  const lastCodeRef = useRef({ code: null, at: 0 });

  const handleCode = async (qr_code) => {
    if (busy) return;
    // Dédoublonnage : ne pas traiter le même code 2× en 3 secondes
    const now = Date.now();
    if (lastCodeRef.current.code === qr_code && now - lastCodeRef.current.at < 3000) return;
    lastCodeRef.current = { code: qr_code, at: now };

    setBusy(true);
    try {
      const { data } = await api.post(`/presences/evenement/${evenementId}/scan`, { qr_code });
      setLastResult(data);
      setRecentScans((arr) => [{ at: now, ...data }, ...arr].slice(0, 10));
      if (data.valide) {
        const name = data.user ? `${data.user.prenom} ${data.user.nom}`.trim() : 'Billet';
        toast.success(`✓ ${name} ${data.already_scanned ? '(re-scan)' : 'émargé'}`);
        onScanned();
      } else {
        toast.error(data.message || 'Billet invalide');
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur scan');
    } finally { setBusy(false); }
  };

  const startScan = async () => {
    setCameraError(null);
    if (scannerRef.current) return;
    try {
      const reader = new Html5Qrcode('scan-reader-emargement');
      scannerRef.current = reader;
      await reader.start(
        { facingMode: 'environment' },
        { fps: 8, qrbox: { width: 280, height: 280 }, aspectRatio: 1 },
        (text) => handleCode(text),
        () => {}
      );
      setScanning(true);
    } catch (err) {
      setCameraError(err?.message || 'Caméra inaccessible');
      scannerRef.current = null;
    }
  };

  const stopScan = async () => {
    if (!scannerRef.current) return;
    try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch {}
    scannerRef.current = null;
    setScanning(false);
  };

  useEffect(() => () => { stopScan(); }, []);

  const submitManual = (e) => {
    e.preventDefault();
    if (!manual.trim()) return;
    handleCode(manual.trim());
    setManual('');
  };

  return (
    <div className="space-y-5">
      {/* Camera */}
      <div className="bg-[#0A192F] rounded-3xl overflow-hidden relative aspect-square max-w-md mx-auto" data-testid="scan-camera">
        <div id="scan-reader-emargement" className="w-full h-full" />
        {!scanning && (
          <div className="absolute inset-0 grid place-items-center bg-[#0A192F] text-white">
            <div className="text-center px-4">
              {cameraError ? (
                <>
                  <AlertCircle className="w-12 h-12 text-[#EF4444] mx-auto mb-3" />
                  <p className="text-sm text-white/80 mb-4">{cameraError}</p>
                  <button onClick={startScan} className="btn-primary"><RefreshCw className="w-4 h-4" /> Réessayer</button>
                </>
              ) : (
                <>
                  <QrCode className="w-12 h-12 text-white/40 mx-auto mb-4" />
                  <p className="text-sm text-white/80 mb-5">Activez la caméra pour scanner les billets.</p>
                  <button onClick={startScan} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-[#0A192F] font-bold hover:bg-white/90 transition-colors" data-testid="start-scan">
                    <Camera className="w-4 h-4" /> Démarrer le scan
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        {scanning && busy && (
          <div className="absolute inset-0 bg-black/50 grid place-items-center">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
        )}
      </div>

      {scanning && (
        <div className="flex justify-center">
          <button onClick={stopScan} className="btn-secondary"><CameraOff className="w-4 h-4" /> Arrêter</button>
        </div>
      )}

      {/* Saisie manuelle code */}
      <form onSubmit={submitManual} className="bg-white border border-[#E2E8F0] rounded-2xl p-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Saisir un code manuellement</div>
        <div className="flex gap-2">
          <input type="text" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Code QR du billet" className="ap-input flex-1" data-testid="manual-code-input" />
          <button type="submit" disabled={!manual.trim() || busy} className="btn-primary !text-sm" data-testid="manual-code-submit">Valider</button>
        </div>
      </form>

      {/* Derniers scans */}
      {recentScans.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-3">Derniers scans</div>
          <div className="space-y-2" data-testid="recent-scans">
            {recentScans.map((s, i) => (
              <div key={i} className={`p-3 rounded-xl text-sm flex items-center gap-3 ${s.valide ? 'bg-[#10B981]/10 text-[#065F46]' : 'bg-[#FEF2F2] text-[#B91C1C]'}`}>
                {s.valide ? <Check className="w-4 h-4 shrink-0" /> : <X className="w-4 h-4 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{s.user ? `${s.user.prenom} ${s.user.nom}` : s.message}</div>
                  {s.already_scanned && <div className="text-[10px] opacity-75">Déjà scanné</div>}
                </div>
                <span className="text-[10px] opacity-60 shrink-0">{new Date(s.at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
